import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type OnboardingStage = 'checking' | 'invitation' | 'tour' | 'complete';
type TourStep = {
  path: string;
  target: string;
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  /** Analytics tab this step needs open before the spotlight can find its target */
  analyticsTab?: 'assessment' | 'progress';
  /** Temporarily selects sample learners so the assignment bar is visible */
  demoLearnerSelection?: boolean;
};
const ONBOARDING_STORAGE_KEY = 'phoebe-onboarding-complete';
const SHOW_ONBOARDING_ON_EVERY_REFRESH = true;
const PAGE_TOUR_STEPS: Record<string, TourStep[]> = {
  '/admin': [{
    path: '/admin',
    target: '[data-phoebe-tour="company-info"]',
    eyebrow: 'Admin Hub · 1 of 5',
    title: 'Know your organization',
    description: 'This card shows your company, registered learners, and pending invitations at a glance.',
    action: 'Keep an eye on pending learners so no one is left waiting to get started.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="send-email"]',
    eyebrow: 'Admin Hub · 2 of 5',
    title: 'Invite pending learners',
    description: 'Use this tool to send an account-creation email to everyone still waiting to register.',
    action: 'Choose one learner or send a reminder to the whole pending group.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="dashboard"]',
    eyebrow: 'Admin Hub · 3 of 6',
    title: 'Read the performance snapshot',
    description: 'Review registration, active learning, and completion to see where attention is needed.',
    action: 'Start with the biggest gap, then choose one follow-up action.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="ai-insight-icon"]',
    eyebrow: 'Admin Hub · 4 of 6',
    title: 'Look for the sparkle for AI insights',
    description: 'Anywhere you see this sparkle icon in the platform, I have an insight about the data next to it — what stands out, why it matters, and what you can do about it.',
    action: 'Hover to read the insight, click to keep it open, and use the follow-up questions to dig deeper with me.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="navigation-cards"]',
    eyebrow: 'Admin Hub · 5 of 6',
    title: 'Explore the rest of the workspace',
    description: 'These cards take you to Analytics, User Management, Reports, and AI-powered insights.',
    action: 'Open whichever area matches the decision you need to make next.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="support"]',
    eyebrow: 'Admin Hub · 6 of 6',
    title: 'Get help when you need it',
    description: 'The support area is always here for questions about the platform or your learners.',
    action: 'Next, let’s look at Analytics to see the story behind your learner data.'
  }],
  '/analytics': [{
    path: '/analytics',
    target: '[data-phoebe-tour="analytics"]',
    eyebrow: 'Analytics · 1 of 5',
    title: 'Find the learning story',
    description: 'Analytics brings together assessment performance, skill patterns, and learner progress so you can decide which learners need support.',
    action: 'Start with the organization-wide view, then narrow to the skills or learners that need attention.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-tabs"]',
    eyebrow: 'Analytics · 2 of 5',
    title: 'Choose the right view',
    description: 'Assessment Analytics helps you understand overall learning quality. Learner Progress shows individual attempt history, improvement, and completion by module.',
    action: 'Use the aggregate view to find a pattern, then switch to Learner Progress to understand who is affected.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-summary"]',
    eyebrow: 'Analytics · 3 of 5',
    title: 'Read the performance signals',
    description: 'Compare learners, pass rate, completion, score, mastery, attempts, and confident errors for a selected skill and time period.',
    action: 'A high confident error rate or falling pass rates usually mean learners need more practice time or better preparation before their next attempt.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-explorer"]',
    eyebrow: 'Analytics · 4 of 5',
    title: 'Explore the pattern behind the metric',
    description: 'Change the chart type, skill, or learner to inspect score distribution, performance by attempt, and answer confidence.',
    action: 'Use confidence data to spot learners who feel sure but are incorrect—an especially useful signal for targeted skill or pathway support.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-table"]',
    eyebrow: 'Analytics · 5 of 5',
    title: 'Turn findings into learner action',
    description: 'Use this table to compare learners, filter the group, and sort by score, improvement, attempts, or confidence signals.',
    action: 'Now let’s open Learner Progress to see how individual learners are doing.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-nav="analytics-tab-progress"]',
    eyebrow: 'Learner Progress · 1 of 4',
    title: 'Switch to the Learner Progress tab',
    description: 'This tab moves you from organization-wide patterns to how each individual learner is doing.',
    action: 'I’ve opened it for you—here’s what to look at first.',
    analyticsTab: 'progress'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-header"]',
    eyebrow: 'Learner Progress · 2 of 4',
    title: 'See every learner, module by module',
    description: 'The Learner Progress tab lists one row per learner and skill module, so you can see exactly where each person stands.',
    action: 'Filter to a single skill module when you want to focus on one area, then download the view to share it.',
    analyticsTab: 'progress'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-scores"]',
    eyebrow: 'Learner Progress · 3 of 4',
    title: 'Compare attempts and improvement',
    description: 'Attempts, first score, final score, and improvement together show whether a learner got there on their own or needed several tries.',
    action: 'Sort by attempts or first score to find learners who are struggling before they reach a passing final score.',
    analyticsTab: 'progress'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-confidence"]',
    eyebrow: 'Learner Progress · 4 of 4',
    title: 'Check confidence alongside accuracy',
    description: 'The confident and correct rates show how sure learners were when they answered right on their first and final attempts.',
    action: 'A low confident-and-correct rate means the learner needs more practice before their next attempt—take them to User Management to assign it.',
    analyticsTab: 'progress'
  }],
  '/feedback': [{
    path: '/feedback',
    target: '[data-phoebe-tour="report-builder"]',
    eyebrow: 'Reports · 1 of 3',
    title: 'Build a report with Phoebe',
    description: 'Tap the sparkle button whenever you want help shaping a custom report.',
    action: 'Tell Phoebe who the report is for and what decision it needs to support.'
  }, {
    path: '/feedback',
    target: '[data-phoebe-tour="reports"]',
    eyebrow: 'Reports · 2 of 3',
    title: 'Start from a template',
    description: 'Choose learner progress, skills analytics, assessment results, or a completion summary.',
    action: 'Templates make it quick to generate a focused update.'
  }, {
    path: '/feedback',
    target: '[data-phoebe-tour="download-reports"]',
    eyebrow: 'Reports · 3 of 3',
    title: 'Find and download reports',
    description: 'Search across learners, skills, and existing reports before downloading.',
    action: 'Next, we’ll head to User Management—the best place to take your first action.'
  }],
  '/assessment': [{
    path: '/assessment',
    target: '[data-phoebe-tour="add-admin"]',
    eyebrow: 'User Management · 1 of 6',
    title: 'Give a colleague admin access',
    description: 'Add Admin invites someone on your team to manage learners, assignments, and reporting alongside you.',
    action: 'Use this when another manager needs to track and support the same group of learners.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="add-individual"]',
    eyebrow: 'User Management · 2 of 6',
    title: 'Add one learner at a time',
    description: 'Add Individual Learner registers a single person with their department, cohort, and starting skills.',
    action: 'Use this for a new hire or a one-off addition to a team you already manage.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="bulk-upload-learners"]',
    eyebrow: 'User Management · 3 of 6',
    title: 'Import a whole group at once',
    description: 'Bulk Upload Learners takes a CSV file and registers everyone in it together.',
    action: 'Use this when you are onboarding a full cohort—you can assign their learning right after the import.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="learner-list"]',
    eyebrow: 'User Management · 4 of 6',
    title: 'Find and select learners',
    description: 'Use search and filters to review your learner list, then select the people who need learning assigned.',
    action: 'For targeted training, start with learners who are unassigned, not started, or need a new pathway.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="select-all"]',
    eyebrow: 'User Management · 5 of 6',
    title: 'Select a page—or everyone',
    description: 'This checkbox selects every learner on the current page. Once it is checked, a link appears to extend the selection to all learners matching your filters, not just this page.',
    action: 'Select the page for a quick batch, or select everyone when you are assigning the same learning organization-wide.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="assignment-bar"]',
    eyebrow: 'User Management · 6 of 7',
    title: 'Assign skills and pathways',
    description: 'Selecting a learner — see the checked box in the table — opens this bar. Assign Skills adds individual modules, Assign Pathways adds a full sequence, and both show how many learners the assignment applies to.',
    action: 'Pick the skills or pathway, then confirm with the Assign button. I have selected one learner here so you can see it.',
    demoLearnerSelection: true
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="skills-overview"]',
    eyebrow: 'User Management · 7 of 7',
    title: 'Review skills and pathways',
    description: 'Check available skill modules and pathways before assigning learning to your selected learners.',
    action: 'Use this overview to confirm capacity before you assign anything.'
  }],
  '/profile': [{
    path: '/profile',
    target: '[data-phoebe-tour="profile"]',
    eyebrow: 'Quick tour · Profile',
    title: 'Manage your profile',
    description: 'Update your personal details, security, display preferences, and notification settings here.',
    action: 'Use the left navigation to switch between General, Security, Preferences, and Settings.'
  }],
  '/alerts': [{
    path: '/alerts',
    target: '[data-phoebe-tour="alerts"]',
    eyebrow: 'Quick tour · Notifications',
    title: 'Stay on top of platform notices',
    description: 'System notices, support replies, account and billing updates, and security activity all arrive here.',
    action: 'Start with anything marked Needs attention, then read and archive the informational notices.'
  }],
  '/insights-actions': [{
    path: '/insights-actions',
    target: '[data-phoebe-tour="insights"]',
    eyebrow: 'Quick tour · Insights',
    title: 'Turn patterns into action',
    description: 'Use AI-supported learning patterns to decide where support will matter most.',
    action: 'Prioritize high-impact insights, then plan one measurable intervention.'
  }],
  '/bulk-upload-results': [{
    path: '/bulk-upload-results',
    target: '[data-phoebe-tour="bulk-upload"]',
    eyebrow: 'Quick tour · Upload Results',
    title: 'Finish a learner import',
    description: 'Review imported learners and assign their first learning pathways before you finish.',
    action: 'Resolve any validation issues, then confirm the learning assignments.'
  }]
};
const TOUR_STEPS: TourStep[] = [...PAGE_TOUR_STEPS['/admin'], ...PAGE_TOUR_STEPS['/analytics'], ...PAGE_TOUR_STEPS['/feedback'], ...PAGE_TOUR_STEPS['/assessment']];
export function PhoebeOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<OnboardingStage>('checking');
  const [stepIndex, setStepIndex] = useState(0);
  const [tourSteps, setTourSteps] = useState<TourStep[]>(TOUR_STEPS);
  const [isPageTour, setIsPageTour] = useState(false);
  const [spotlight, setSpotlight] = useState<DOMRect | null>(null);
  const step = tourSteps[stepIndex];
  const isAdminHub = location.pathname === '/admin';
  const isLastStep = stepIndex === tourSteps.length - 1;
  const nextStep = tourSteps[stepIndex + 1];
  const nextLabel = !isPageTour && nextStep && nextStep.path !== step.path ? {
    '/analytics': 'Go to Analytics',
    '/feedback': 'Go to Reports',
    '/assessment': 'Go to User Management'
  }[nextStep.path] || 'Next' : 'Next';
  useEffect(() => {
    const hasCompletedOnboarding = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    setStage(SHOW_ONBOARDING_ON_EVERY_REFRESH || !hasCompletedOnboarding ? 'invitation' : 'complete');
  }, []);
  useEffect(() => {
    const startPageTour = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      const pageSteps = PAGE_TOUR_STEPS[path];
      if (!pageSteps) return;
      setTourSteps(pageSteps);
      setIsPageTour(true);
      setStepIndex(0);
      setStage('tour');
      navigate(pageSteps[0].path);
    };
    const startPlatformTour = () => {
      setTourSteps(TOUR_STEPS);
      setIsPageTour(false);
      setStepIndex(0);
      setStage('tour');
      navigate(TOUR_STEPS[0].path);
    };
    // Opening the chat or triggering an insight means the intro is no longer needed
    const dismissInvitation = () => setStage(current => current === 'invitation' ? 'complete' : current);
    window.addEventListener('phoebe:page-tour', startPageTour);
    window.addEventListener('phoebe:platform-tour', startPlatformTour);
    window.addEventListener('phoebe:chat-opened', dismissInvitation);
    return () => {
      window.removeEventListener('phoebe:page-tour', startPageTour);
      window.removeEventListener('phoebe:platform-tour', startPlatformTour);
      window.removeEventListener('phoebe:chat-opened', dismissInvitation);
    };
  }, [navigate]);
  useEffect(() => {
    if (stage !== 'invitation') return;
    const timeoutId = window.setTimeout(() => setStage('complete'), 30_000);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, stage]);

  // Highlight the nav item (and analytics tab) the current step belongs to
  useEffect(() => {
    const clearHighlights = () => {
      document.querySelectorAll('.phoebe-nav-active').forEach(element => element.classList.remove('phoebe-nav-active'));
    };
    if (stage !== 'tour') {
      clearHighlights();
      return;
    }

    // Only the page-level nav item is highlighted; tabs get their own
    // spotlight step so two highlights never compete on screen
    const navKeys = [step.path];
    const applyHighlights = () => {
      clearHighlights();
      navKeys.forEach(key => {
        document.querySelectorAll(`[data-phoebe-nav="${key}"]`).forEach(element => element.classList.add('phoebe-nav-active'));
      });
    };
    applyHighlights();
    // The nav may still be mounting right after a route change
    const timeoutId = window.setTimeout(applyHighlights, 200);
    return () => {
      window.clearTimeout(timeoutId);
      clearHighlights();
    };
  }, [location.pathname, stage, step.path]);
  useEffect(() => {
    if (stage !== 'tour') return;
    if (step.analyticsTab) {
      window.dispatchEvent(new CustomEvent('phoebe:analytics-tab', {
        detail: step.analyticsTab
      }));
    }
    window.dispatchEvent(new CustomEvent('phoebe:demo-learner-selection', {
      detail: Boolean(step.demoLearnerSelection)
    }));
    const timeouts: number[] = [];
    const updateSpotlight = (attempt = 0) => {
      const target = document.querySelector(step.target);
      if (!target) {
        setSpotlight(null);
        // The target may belong to a tab that is still rendering
        if (attempt < 5) {
          timeouts.push(window.setTimeout(() => updateSpotlight(attempt + 1), 120));
        }
        return;
      }
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      timeouts.push(window.setTimeout(() => setSpotlight(target.getBoundingClientRect()), 250));
    };
    const handleReposition = () => updateSpotlight();
    updateSpotlight();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      timeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [location.pathname, stage, step.target, step.analyticsTab, step.demoLearnerSelection]);

  // Clear the sample selection once the tour ends
  useEffect(() => {
    if (stage === 'tour') return;
    window.dispatchEvent(new CustomEvent('phoebe:demo-learner-selection', {
      detail: false
    }));
  }, [stage]);
  const spotlightStyle = useMemo(() => {
    if (!spotlight) return undefined;
    const inset = 8;
    return {
      top: Math.max(inset, spotlight.top - inset),
      left: Math.max(inset, spotlight.left - inset),
      width: Math.min(window.innerWidth - inset * 2, spotlight.width + inset * 2),
      height: Math.min(window.innerHeight - inset * 2, spotlight.height + inset * 2)
    };
  }, [spotlight]);
  const completeOnboarding = () => {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setStage('complete');
  };
  const startTour = () => {
    setTourSteps(TOUR_STEPS);
    setIsPageTour(false);
    setStepIndex(0);
    setStage('tour');
    navigate(TOUR_STEPS[0].path);
  };
  const startInvitationTour = () => {
    if (isAdminHub) {
      startTour();
      return;
    }
    const pageSteps = PAGE_TOUR_STEPS[location.pathname];
    if (!pageSteps) return;
    setTourSteps(pageSteps);
    setIsPageTour(true);
    setStepIndex(0);
    setStage('tour');
  };
  const moveToStep = (nextIndex: number) => {
    setStepIndex(nextIndex);
    navigate(tourSteps[nextIndex].path);
  };
  const finishTour = () => {
    if (isPageTour) {
      setStage('complete');
      openPhoebeChat({
        prompt: `I just took a quick tour of ${step.eyebrow.replace('Quick tour · ', '')}. What should I do next?`,
        response: `**You’re all set**\n\nYou’ve seen the key area of this page. I can help you take the next step, answer a question, or explain a specific control.`,
        suggestedQuestions: ['Help me choose my next step', 'Explain this page in more detail']
      });
      return;
    }
    completeOnboarding();
    navigate('/assessment');
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('phoebe:assign-learning'));
    }, 180);
  };
  if (stage === 'checking' || stage === 'complete') return null;
  return <AnimatePresence data-id="element-2067" data-mp="1ifmtd2iksanr">
      {stage === 'invitation' && <motion.section initial={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="fixed bottom-20 right-4 z-[60] w-[min(17rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg sm:bottom-24 sm:right-6" role="dialog" aria-labelledby="phoebe-welcome-title" data-id="element-2068" data-mp="1dfibb820r7vf">
          <button type="button" onClick={() => setStage('complete')} className="absolute -right-2.5 -top-2.5 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" aria-label={isAdminHub ? "Dismiss Phoebe's platform tour invitation" : "Dismiss Phoebe's page tour invitation"} data-id="element-2069" data-mp="un67shzmq0jp">
            <X size={15} aria-hidden="true" data-id="element-2070" data-mp="2qni0mb6mfspj" />
          </button>
          <button type="button" onClick={startInvitationTour} className="w-full text-left focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2071" data-mp="2qmzsohaljq1s">
            <p className="text-xs font-bold text-[#5E7079]" data-id="element-2072" data-mp="30jl0tbhwxh9u">Phoebe · AI assistant</p>
            <h2 id="phoebe-welcome-title" className="mt-1 text-sm leading-snug text-[#001823]" data-id="element-2073" data-mp="1a9rs8s8gyia3">
              {isAdminHub ? 'Hi, I’m Phoebe. Want a quick tour of the whole platform?' : 'Hi, I’m Phoebe. Want to learn more about this screen?'}
            </h2>
          </button>
        </motion.section>}

      {stage === 'tour' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="phoebe-tour-title" data-id="element-2074" data-mp="mdxxiy810rhl">
          {spotlightStyle ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="pointer-events-none fixed z-[71] rounded-xl border-2 border-[#30718D] bg-white/5 shadow-[0_0_0_9999px_rgba(3,7,18,0.45)]" style={spotlightStyle} data-id="element-2075" data-mp="heldsmcgc0j8" /> : <div className="absolute inset-0 bg-gray-950/45" data-id="element-2076" data-mp="1xstmpe4aubce" />}
          <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed bottom-4 left-4 right-4 z-[72] mx-auto w-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl sm:bottom-6 sm:left-auto sm:right-6 sm:w-[30rem]" data-id="element-2077" data-mp="22s66fpzvj2ar">
            <div className="flex items-start justify-between gap-4" data-id="element-2078" data-mp="1rexkkruboiyt">
              <div className="flex items-center gap-2 text-[#30718D]" data-id="element-2079" data-mp="1mf1n7bg4rps8">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D9EEF2]" data-id="element-2080" data-mp="2k1s0dbg72u5y">
                  <Sparkles size={16} aria-hidden="true" data-id="element-2081" data-mp="2f25op6g0llz9" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.1em]" data-id="element-2082" data-mp="2abfjbt7l9j4k">Phoebe’s tour</p>
              </div>
              <button type="button" onClick={completeOnboarding} className="text-sm font-semibold text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-2083" data-mp="25de0n9e2rltm">
                Skip tour
              </button>
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500" data-id="element-2084" data-mp="35eslmjpwmotw">{step.eyebrow}</p>
            <h2 id="phoebe-tour-title" className="mt-1 text-lg font-bold text-gray-900" data-id="element-2085" data-mp="3favmlm90shf9">{step.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-2086" data-mp="3k9f67g055h4g">{step.description}</p>
            <div className="mt-3 rounded-lg bg-[#30718D]/5 px-3 py-2 text-xs leading-relaxed text-[#265A70]" data-id="element-2087" data-mp="3obk08fui4frm">{step.action}</div>
            <div className="mt-5 flex items-center justify-between gap-3" data-id="element-2088" data-mp="3t9brd7nbnxur">
              <button type="button" onClick={() => moveToStep(stepIndex - 1)} disabled={stepIndex === 0} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-2089" data-mp="1vzcqk82mo62">
                <ArrowLeft size={16} aria-hidden="true" data-id="element-2090" data-mp="1djbnuql4vepi" /> Back
              </button>
              {isLastStep ? <button type="button" onClick={finishTour} className="inline-flex items-center gap-2 rounded-lg bg-[#30718D] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2091" data-mp="18kqyxnbc0hgd">
                  {isPageTour ? 'Got it' : 'Assign skills & pathways'} <ArrowRight size={16} aria-hidden="true" data-id="element-2092" data-mp="pnz7urpjsy1r" />
                </button> : <button type="button" onClick={() => moveToStep(stepIndex + 1)} className="inline-flex items-center gap-2 rounded-lg bg-[#30718D] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2093" data-mp="13nh05h21ytfv">
                  {nextLabel} <ArrowRight size={16} aria-hidden="true" data-id="element-2094" data-mp="2iigu9i36y08g" />
                </button>}
            </div>
          </motion.section>
        </motion.div>}
    </AnimatePresence>;
}